import React, { useState, useEffect } from 'react';
import { DEMO_USER, DEMO_USER_RESP } from '../lib/demo.js';

// Mode démo : JAMAIS sur la prod (il contourne le PIN et lit les vraies
// données). Autorisé en dev local et sur les URL de prévisualisation Netlify.
const DEMO_ENABLED = import.meta.env.DEV
  || /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)
  || /--elixirentrepot\.netlify\.app$/.test(window.location.hostname);

const AUTH_URL = '/.netlify/functions/auth';

async function authCall(action, params) {
  const res = await fetch(AUTH_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include', // pour envoyer/recevoir les cookies
    body: JSON.stringify({ action, params }),
  });
  const json = await res.json();
  if (json.error) throw new Error(json.error);
  return json.result;
}

export default function Login({ onLogin }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { loadUsers(); checkExistingSession(); }, []);

  async function checkExistingSession() {
    try {
      const result = await authCall('verify', {});
      if (result.valid && result.user) {
        try { if (result.supaJwt) localStorage.setItem('elixir_supa_jwt', result.supaJwt); } catch {}
        onLogin(result.user);
      }
    } catch (e) { /* pas de session valide — normal */ }
  }

  async function loadUsers() {
    try {
      const result = await authCall('listUsers', {});
      setUsers(result || []);
    } catch (e) {
      setError('Impossible de charger les utilisateurs.');
    } finally {
      setLoading(false);
    }
  }

  const [selectedUser, setSelectedUser] = useState(null);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const [pinMessage, setPinMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  function handleSelect(user) {
    if (user.hasPin) {
      setSelectedUser(user);
      setPin('');
      setPinError(false);
      setPinMessage('');
    } else {
      doLogin(user.id, '');
    }
  }

  async function doLogin(userId, pinCode) {
    setSubmitting(true);
    setPinError(false);
    setPinMessage('');
    try {
      const result = await authCall('login', { userId, pin: pinCode });
      if (result.error) {
        setPinError(true);
        setPinMessage(result.rateLimited ? result.error : 'Code incorrect — réessaie');
        setPin('');
        try { navigator.vibrate?.([100, 50, 100]); } catch {}
      } else if (result.success && result.user) {
        try { if (result.supaJwt) localStorage.setItem('elixir_supa_jwt', result.supaJwt); } catch {}
        onLogin(result.user);
      }
    } catch (e) {
      console.warn('Auth server unavailable:', e);
      setPinError(true);
      setPinMessage('Connexion indisponible. Réessaie dans un instant.');
      setPin('');
    }
    setSubmitting(false);
  }

  function handlePinChange(val) {
    const clean = val.replace(/\D/g, '').slice(0, 4);
    setPin(clean);
    setPinError(false);
    setPinMessage('');
    if (clean.length === 4 && selectedUser) {
      setTimeout(() => doLogin(selectedUser.id, clean), 150);
    }
  }

  const magasiniers = users.filter(u => u.role === 'magasinier');
  const responsables = users.filter(u => ['responsable', 'pharmacien', 'admin'].includes(u.role));

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.logo}>ELIXIR PREP</div>
          <div style={styles.subtitle}>Sélectionne ton nom pour commencer</div>
        </div>

        <div style={styles.body}>
          {loading && <div style={styles.loading}>Chargement…</div>}
          {error && <div style={styles.error}>{error}</div>}

          {/* ═══ PIN SCREEN ═══ */}
          {selectedUser && (
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%', margin: '0 auto 12px',
                background: selectedUser.role === 'admin' ? '#7D3C98' : selectedUser.role === 'pharmacien' ? '#b5904a' : selectedUser.role === 'responsable' ? '#1E6B3C' : '#1A5276',
                color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, fontWeight: 700,
              }}>{selectedUser.name.charAt(0).toUpperCase()}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#18181A', marginBottom: 4 }}>{selectedUser.name}</div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>Entre ton code PIN</div>

              <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 16 }}>
                {[0, 1, 2, 3].map(i => (
                  <div key={i} style={{
                    width: 18, height: 18, borderRadius: '50%',
                    background: i < pin.length ? (pinError ? '#C0392B' : '#1A5276') : 'transparent',
                    border: '2px solid ' + (pinError ? '#C0392B' : i < pin.length ? '#1A5276' : '#D1D5DB'),
                    transition: 'all 0.15s',
                  }}></div>
                ))}
              </div>

              {pinError && (
                <div style={{ fontSize: 12, color: '#C0392B', fontWeight: 600, marginBottom: 12 }}>
                  {pinMessage || 'Code incorrect — réessaie'}
                </div>
              )}

              {submitting && (
                <div style={{ fontSize: 12, color: '#1A5276', fontWeight: 600, marginBottom: 12 }}>
                  Vérification…
                </div>
              )}

              <input type="tel" inputMode="numeric" value={pin} onChange={e => handlePinChange(e.target.value)}
                autoFocus maxLength={4} disabled={submitting}
                style={{ position: 'absolute', opacity: 0, width: 1, height: 1 }} />

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, maxWidth: 240, margin: '0 auto' }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, 'del'].map((n, i) => {
                  if (n === null) return <div key={i}></div>;
                  return (
                    <button key={i} disabled={submitting} onClick={() => {
                      if (n === 'del') { setPin(p => p.slice(0, -1)); setPinError(false); }
                      else if (pin.length < 4) handlePinChange(pin + n);
                    }} style={{
                      padding: '14px 8px', borderRadius: 10, border: '1.5px solid #E8E8EE',
                      background: 'white', fontSize: n === 'del' ? 14 : 20, fontWeight: 600,
                      cursor: submitting ? 'wait' : 'pointer', fontFamily: 'inherit', color: '#18181A',
                      opacity: submitting ? 0.5 : 1,
                    }}>{n === 'del' ? '←' : n}</button>
                  );
                })}
              </div>

              <button onClick={() => { setSelectedUser(null); setPin(''); setPinError(false); }}
                style={{ marginTop: 16, padding: '8px 20px', borderRadius: 8, border: 'none', background: 'transparent', color: '#888', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
                ← Changer d'utilisateur
              </button>
            </div>
          )}

          {!loading && !error && !selectedUser && (
            <>
              {DEMO_ENABLED && <div style={{ display: 'flex', gap: 8, padding: '0 0 12px' }}>
                <button style={{ flex: 1, padding: '10px 8px', borderRadius: 10, border: '1.5px solid #F0C36D', background: '#FFFDE7', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center' }}
                  onClick={() => onLogin(DEMO_USER)}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#D4680A' }}>🧪 Essayer l'app</div>
                  <div style={{ fontSize: 9, color: '#92710C', marginTop: 2 }}>Données fictives — sans risque</div>
                </button>
              </div>}

              <div style={styles.section}>MAGASINIERS</div>
              <div style={styles.grid}>
                {magasiniers.map(u => (
                  <button key={u.id} style={styles.userBtn} onClick={() => handleSelect(u)}>
                    <div style={{ ...styles.avatar, background: '#1A5276' }}>{u.name.charAt(0).toUpperCase()}</div>
                    <div style={styles.userName}>{u.name}</div>
                  </button>
                ))}
              </div>

              <div style={{ ...styles.section, marginTop: 16 }}>RESPONSABLES</div>
              <div style={styles.grid}>
                {responsables.map(u => (
                  <button key={u.id} style={styles.userBtn} onClick={() => handleSelect(u)}>
                    <div style={{ ...styles.avatar, background: '#1E6B3C' }}>{u.name.charAt(0).toUpperCase()}</div>
                    <div style={styles.userName}>{u.name}</div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {DEMO_ENABLED && !selectedUser && (
          <div style={{ padding: '0 18px 12px', textAlign: 'center' }}>
            <button onClick={() => onLogin(DEMO_USER_RESP)} style={{ background: 'none', border: 'none', color: '#9CA3AF', fontSize: 10, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}>
              Essayer en mode responsable
            </button>
          </div>
        )}

        <div style={styles.footer}>Elixir Pharma — Entrepôt Écquevilly</div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#18181A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  card: {
    background: 'white',
    borderRadius: 20,
    width: '100%',
    maxWidth: 420,
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: {
    background: '#18181A',
    padding: '24px 20px 18px',
    textAlign: 'center',
  },
  logo: {
    color: 'white',
    fontSize: 22,
    fontWeight: 800,
    letterSpacing: '0.08em',
  },
  subtitle: {
    color: '#888',
    fontSize: 12,
    marginTop: 6,
  },
  body: {
    padding: '16px 18px',
  },
  section: {
    fontSize: 10,
    fontWeight: 700,
    color: '#999',
    letterSpacing: '0.06em',
    marginBottom: 8,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 8,
  },
  userBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 14px',
    background: '#F8F8FA',
    border: '1.5px solid #E8E8EE',
    borderRadius: 10,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left',
    transition: 'all 0.12s',
    WebkitTapHighlightColor: 'transparent',
  },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 700,
    flexShrink: 0,
  },
  userName: {
    fontSize: 13,
    fontWeight: 600,
    color: '#18181A',
  },
  footer: {
    padding: '12px 20px',
    textAlign: 'center',
    fontSize: 10,
    color: '#BBB',
    borderTop: '1px solid #F0F0F0',
  },
  loading: {
    textAlign: 'center',
    padding: '30px 0',
    color: '#999',
    fontSize: 14,
  },
  error: {
    textAlign: 'center',
    padding: '20px',
    color: '#C0392B',
    fontSize: 13,
    background: '#FDECEA',
    borderRadius: 10,
  },
};
